/**
 * LEGEND PLANTS — the species a player has harvested 10,000 of, and the prestige SPARKLE they earn.
 *
 * ── WHAT IT IS ──
 *
 * The top rung of every per-plant harvest ladder ("<species> Legend", 10,000 harvested, 10 points — the
 * scarcest achievement in the game) pays no coins and no items. It pays a permanent, visible change to the world:
 * from then on EVERY plant of that species, in the greenhouse and in the garden, gives off a drifting
 * sparkle. It is a trophy you cannot put in a cupboard — the reward for the grind is that your garden looks
 * different from everyone else's, forever.
 *
 * NON-TANGIBLE ON PURPOSE. A 10,000-harvest achievement paying currency would either be trivial (next to what
 * 10,000 harvests already earned) or economy-breaking. A cosmetic has no such ceiling, and it is the only
 * reward whose value goes UP the more of the plant you own.
 *
 * ── WHY THIS LIVES IN SIM AND NOT IN THE RENDERER ──
 *
 * Two scenes draw plants (RackLayer for the greenhouse, GardenGroundLayer for the garden) and both need the
 * same answer to "does this species sparkle?". Deriving it in each would be two copies of a rule keyed off
 * generated achievement ids — and the ids are the one thing achievementFamilies.ts is emphatic must stay stable.
 * So the derivation is here, once, and both renderers ask.
 *
 * ── THE PLAYER CAN TURN IT OFF ──
 *
 * The effect is deliberately loud, and loud is not for everyone. `HudPrefs.legendSparkle` gates it from the
 * Advanced Settings menu; this module does not know about that (it answers what has been EARNED), and the
 * renderers check the pref. Keeping the two separate means turning the sparkle off never looks like losing
 * the achievement.
 */

import { HARVEST_METRIC, HARVEST_TIERS } from "./achievementFamilies";
import type { FlowerId } from "./flowers";

/** The harvest count that earns the sparkle — the TOP rung of the ladder, read from the tier table rather
 *  than written as 10000, so a re-tune of the ladder moves this with it. */
export const LEGEND_THRESHOLD = HARVEST_TIERS[HARVEST_TIERS.length - 1]!.n;

/**
 * The species whose LEGEND achievement is unlocked, out of a player's unlocked achievement ids.
 *
 * Parsing the id is deliberate and is the one place it happens. `harvestAchievementId` builds
 * `harvested_<species>_<n>`, and a species id can itself contain no underscore (they are camelCase — see
 * flowers.ts), so splitting on the LAST underscore is unambiguous. The alternative — asking the achievement
 * registry for each achievement's conditions — would mean this module could not answer until the generated
 * families were registered, which happens at boot in main.tsx and not at all in a headless test.
 */
export function legendSpecies(unlocked: Iterable<string>): Set<FlowerId> {
  const out = new Set<FlowerId>();
  const prefix = `${HARVEST_METRIC}_`;
  const suffix = `_${LEGEND_THRESHOLD}`;
  for (const id of unlocked) {
    if (!id.startsWith(prefix) || !id.endsWith(suffix)) continue;
    const species = id.slice(prefix.length, id.length - suffix.length);
    if (species) out.add(species as FlowerId);
  }
  return out;
}

// ── THE EFFECT'S BUDGET ────────────────────────────────────────────────────────────────────────────────────

/**
 * THE GLOBAL MOTE CAP, and the reason the whole effect is affordable.
 *
 * A player who has legend-ranked a common species can have well over a hundred of it on screen at once. At a
 * fixed per-plant emission rate that is a blizzard — visually, and for the frame budget (the greenhouse runs
 * at ~3.4ms against a 4.0ms ceiling, so there is well under a millisecond to spend).
 *
 * So the effect is budgeted GLOBALLY rather than per plant: the scene may have at most `MAX_MOTES` alive,
 * and each plant's share is that cap divided between however many legend plants are actually on screen.
 * The consequences are the point:
 *   • With a handful of legend plants each one sparkles HARD — which is exactly when the player has just
 *     earned it and most wants to see it.
 *   • With a hundred, each emits slowly, and the SCENE still carries the same total shimmer. It reads as a
 *     field of fireflies rather than as a hundred separate firehoses.
 *   • The cost is flat either way. That is what makes "noticeable" and "100+ on screen" compatible at all.
 *
 * `MIN_RATE` stops the division starving a plant completely — below roughly one mote every few seconds an
 * individual plant stops reading as special, which would defeat the achievement.
 */
export const MAX_MOTES = 200;

/**
 * THE PER-PLANT CEILING, and it is the limit that actually bites for most players.
 *
 * TWO CAPS, NOT ONE. `MAX_MOTES` bounds the SCENE, which is what protects the frame budget; this bounds one
 * PLANT, which is what protects the plant. They are different problems and conflating them gets one of them
 * wrong: a player with a single legend daisy must not have the whole 200-mote budget dumped onto it — that
 * is a firework, not a flower, and the plant disappears behind its own reward. So a plant never carries more
 * than `MAX_MOTES_PER_PLANT` alive at once, however much headroom the scene has.
 *
 * Expressed as a COUNT rather than a rate because that is the thing being reasoned about ("how many sparks
 * are on this daisy"); the rate follows from it and the lifetime.
 */
export const MAX_MOTES_PER_PLANT = 5;
/** Floor on the per-plant rate however crowded the scene gets (motes/sec) — below about this, an individual
 *  plant stops reading as special at all, which would defeat the achievement. */
export const MIN_RATE_PER_PLANT = 0.55;
/** How long one mote lives (seconds). Cap ÷ (rate × life) is what bounds the live count.
 *  SHORT on purpose: a mote that lives too long drifts far from the plant that threw it, and a scatter of
 *  sparks halfway across the garden reads as a bug rather than as a plant sparkling. */
export const MOTE_LIFE = 1.7;

/** Motes per second one plant emits to hold `MAX_MOTES_PER_PLANT` alive (alive ≈ rate × life). */
export const MAX_RATE_PER_PLANT = MAX_MOTES_PER_PLANT / MOTE_LIFE;

/**
 * The per-plant emission rate (motes/sec) for `n` legend plants currently on screen.
 *
 * THE SHARE IS EVEN, AND BOUNDED AT BOTH ENDS. Every plant gets the same rate — no plant is ever favoured
 * over another, which would look arbitrary — and that rate is the SMALLER of:
 *   • its fair slice of the scene budget, `MAX_MOTES / (life × n)`, which is what keeps a hundred plants
 *     affordable; and
 *   • `MAX_RATE_PER_PLANT`, which is what stops one lonely plant being buried in its own sparkle.
 * Then floored at MIN_RATE_PER_PLANT so a very crowded scene still gives every plant an occasional glint.
 *
 * In practice the per-plant ceiling governs up to ~23 plants and the scene budget governs past that, which
 * is the intended handover: small collections look identical however many you have, and large ones thin out
 * evenly rather than any one plant going dark.
 */
export function moteRateFor(n: number): number {
  if (n <= 0) return 0;
  const fairShare = MAX_MOTES / (MOTE_LIFE * n);
  return Math.max(MIN_RATE_PER_PLANT, Math.min(MAX_RATE_PER_PLANT, fairShare));
}
